import { useEffect, useState } from "react";
import { appConfirm } from "../../components/ConfirmDialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";

const ROLES = [
  ["site_manager", "Site Manager", "Client job files only"],
  ["office_staff", "Office Staff", "Everything except settings, staff & financials"],
  ["admin", "Administrator", "Full access except financials"],
  ["senior_admin", "Senior Admin", "Full control incl. financials (owner)"],
];
const roleLabel = (r) => (ROLES.find(x => x[0] === r) || [r, r])[1];

export function StaffPanel({ meEmail, meRole }) {
  const isSenior = meRole === "senior_admin";
  const roleOptions = isSenior ? ROLES : ROLES.filter(([v]) => v !== "senior_admin");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "site_manager" });

  const load = () => api.get("/admin/staff").then(r => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.password) { toast.error("Email and password required"); return; }
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff member added");
      setForm({ email: "", name: "", password: "", role: "site_manager" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const setRole = async (email, role) => {
    try { await api.put(`/admin/staff/${email}`, { role }); load(); toast.success("Role updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/admin/staff/${u.email}`, { active: u.active === false }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const resetPw = async (email) => {
    const pw = window.prompt(`New password for ${email} (min 6 chars):`);
    if (!pw) return;
    try { await api.put(`/admin/staff/${email}`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reset"); }
  };
  const del = async (email) => {
    if (!(await appConfirm({ title: `Delete ${email}?`, confirmLabel: "Delete", destructive: true }))) return;
    try { await api.delete(`/admin/staff/${email}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div data-testid="staff-panel">
      <div className="border border-[#E5E7EB] bg-white p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div><div className="label-eyebrow mb-1">Name</div><input data-testid="staff-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full" /></div>
        <div><div className="label-eyebrow mb-1">Email</div><input data-testid="staff-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full" /></div>
        <div><div className="label-eyebrow mb-1">Password</div><input data-testid="staff-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full" /></div>
        <div><div className="label-eyebrow mb-1">Role</div>
          <select data-testid="staff-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full bg-white">
            {roleOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <button data-testid="staff-create" onClick={create} className="de-btn-primary justify-center"><UserPlus size={16} /> Add</button>
      </div>

      <div className="overflow-x-auto border border-[#E5E7EB] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F4F4] text-left"><tr>{["Name", "Email", "Role", "Status", "Actions"].map(h => <th key={h} className="px-3 py-2 text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
          <tbody>
            {items.map(u => (
              <tr key={u.email} data-testid={`staff-row-${u.email}`} className="border-t border-[#E5E7EB]">
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2">{u.email}{u.email === meEmail && <span className="text-[10px] text-[#C79A3B] ml-1">(you)</span>}</td>
                <td className="px-3 py-2">
                  <select data-testid={`staff-role-${u.email}`} value={u.role} disabled={u.role === "senior_admin" && !isSenior} onChange={e => setRole(u.email, e.target.value)} className="border border-[#E5E7EB] px-2 py-1 text-xs bg-white disabled:opacity-60">
                    {(roleOptions.some(([v]) => v === u.role) ? roleOptions : [...roleOptions, [u.role, roleLabel(u.role)]]).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <button data-testid={`staff-active-${u.email}`} onClick={() => toggleActive(u)} className={`text-xs font-bold uppercase ${u.active === false ? "text-[#6B7280]" : "text-green-700"}`}>{u.active === false ? "Disabled" : "Active"}</button>
                </td>
                <td className="px-3 py-2 flex gap-3">
                  <button data-testid={`staff-reset-${u.email}`} onClick={() => resetPw(u.email)} className="text-xs underline hover:text-[#C79A3B]">Reset pw</button>
                  {u.email !== meEmail && <button data-testid={`staff-delete-${u.email}`} onClick={() => del(u.email)} className="text-[#6B7280] hover:text-[#C79A3B]"><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#6B7280] mt-3">{ROLES.map(([, l, d]) => `${l}: ${d}`).join(" · ")}</p>
    </div>
  );
}
